import { Box } from "@dropbox/dig-foundations";
import { tagged } from "@mirage/service-logging";
import { previewStack } from "@mirage/service-stacks";
import { useEffect, useState } from "react";
import { StackChatHeader } from "./StackChatHeader";

import type { stacks } from "@dropbox/api-v2-client";
import type { ConversationRouterState } from "@mirage/service-conversation/router_state_types";
import type { StackItemShortcutWithMetadata } from "@mirage/service-stacks/service/types";
import { useEnrichedStackItems } from "@mirage/stacks/hooks";
import { getStackIconUrl } from "@mirage/stacks/utils";
import type { StackChatHeaderData } from "./StackChatHeader";
import styles from "./StackChatPanel.module.css";
import { StackSourcesAccordion } from "./StackSourcesAccordion";

const logger = tagged("stack-chat-panel");

type StackChatPanelProps = {
  chatSessionId?: string;
  lastUserMessage: string;
  onRefreshConversation: (routerState?: ConversationRouterState) => void;
  serverConversationId?: string;
  sourceDocumentIds: string[];
  stackSharingId: string;
};

export function StackChatPanel({
  chatSessionId,
  lastUserMessage,
  onRefreshConversation,
  serverConversationId,
  sourceDocumentIds,
  stackSharingId,
}: StackChatPanelProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [headerData, setHeaderData] = useState<StackChatHeaderData | null>(
    null,
  );

  // Store the namespace id and unenriched stack items for use in item enrichment
  const [stackNamespaceId, setStackNamespaceId] = useState<string | undefined>(
    undefined,
  );
  const [stackItems, setStackItems] = useState<stacks.StackItem[]>([]);

  // Enrich the stack items returned from PreviewStack so we can map sources
  const enrichedStackItems = useEnrichedStackItems(
    stackNamespaceId,
    stackItems,
  ) as StackItemShortcutWithMetadata[];

  useEffect(() => {
    async function fetchStackPreview() {
      setIsLoading(true);

      try {
        const { stack, items } = await previewStack(stackSharingId);

        setStackItems(items);
        setStackNamespaceId(stack.namespace_id);

        const creator = stack.sharing_data?.members?.find((member) => {
          return member.permission?.[".tag"] === "owner";
        });

        setHeaderData({
          colorIndex: stack.stack_data?.color_index,
          creatorDisplayName:
            creator?.subject?.[".tag"] === "user"
              ? creator.subject.display_name
              : "",
          creatorProfileImgUrl:
            creator?.subject?.[".tag"] === "user"
              ? creator.subject.profile_photo_url
              : "",
          customColor: stack.stack_data?.custom_color,
          emoji: stack.stack_data?.emoji,
          memberCount: stack.sharing_data?.members?.length || 0,
          name: stack.stack_data?.name || "",
          iconUrl: getStackIconUrl(stack),
        });
      } catch (e) {
        // TODO: Determine how we want to handle this.
        logger.error("Call to PreviewStack failed", e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    }

    void fetchStackPreview();
  }, [stackSharingId]);

  return (
    <Box className={styles.stackChatPanel}>
      <StackChatHeader data={headerData} isLoading={isLoading} />
      <StackSourcesAccordion
        chatSessionId={chatSessionId}
        documentIds={sourceDocumentIds}
        enrichedStackItems={enrichedStackItems}
        isLoading={isLoading}
        lastUserMessage={lastUserMessage}
        onRefreshConversation={onRefreshConversation}
        serverConversationId={serverConversationId}
        sharingId={stackSharingId}
        namespaceId={stackNamespaceId || ""}
      />
    </Box>
  );
}
